import { resolveServiceTopic, type ServiceTopicRoute } from '../../../config/serviceTopicRoutes';
import { SELL_VEHICLE_DRAFT_MESSAGE } from '../../cars/carInventoryTypes';
import type { HomeTopicCard, HomeTopicSectionConfig } from '../homeTopicSections';

const PAGE_PATH_RE = /^\/[a-zA-Z0-9/_-]*$/;
const CANADA_ONLY_PATHS = ['/vehicles'];

function isCanadaOnlyPath(path: string): boolean {
  return CANADA_ONLY_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

function isPagePathAllowedForCountry(path: string, country?: string): boolean {
  if (country === 'IN' && isCanadaOnlyPath(path)) return false;
  return true;
}

export type HomeItemClickResult =
  | { type: 'page'; path: string }
  | { type: 'thread'; topic: ServiceTopicRoute };

function sanitizePagePath(path?: string): string {
  const raw = (path || '').trim();
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('://')) return '';
  if (!PAGE_PATH_RE.test(raw)) return '';
  return raw;
}

function defaultPagePath(slug: string): string {
  if (slug === 'buy-vehicle') return '/vehicles';
  if (slug === 'esim' || slug === 'canada-esim' || slug === 'buy-esim') return '/store/canada-esim';
  return '';
}

function findHomeItem(
  categoryId: string,
  slug: string,
  sections: HomeTopicSectionConfig[]
): HomeTopicCard | undefined {
  const cat = categoryId.toLowerCase();
  const key = slug.toLowerCase();
  for (const section of sections) {
    const match = section.items.find(
      (item) => item.categoryId.toLowerCase() === cat && item.slug.toLowerCase() === key
    );
    if (match) return match;
  }
  return undefined;
}

export function homeItemOpensPage(
  item?: Pick<HomeTopicCard, 'clickAction' | 'slug' | 'pagePath'> | null,
  country?: string
): boolean {
  if (!item) return false;
  const wantsPage = item.clickAction === 'page' || (!item.clickAction && item.slug === 'buy-vehicle');
  if (!wantsPage) return false;
  const path = sanitizePagePath(item.pagePath) || defaultPagePath(item.slug);
  return Boolean(path && isPagePathAllowedForCountry(path, country));
}

/** Buy a Vehicle opens the marketplace; other items start a chat unless admin set a page. */
export function resolveHomeItemClick(
  categoryId: string,
  slug: string,
  sections: HomeTopicSectionConfig[],
  country?: string
): HomeItemClickResult | null {
  const item = findHomeItem(categoryId, slug, sections);
  const wantsPage = item?.clickAction === 'page' || (!item?.clickAction && slug === 'buy-vehicle');
  if (wantsPage) {
    const path = sanitizePagePath(item?.pagePath) || defaultPagePath(slug);
    if (path && !isPagePathAllowedForCountry(path, country)) return null;
    if (path) return { type: 'page', path };
  }

  const topic =
    resolveServiceTopic(categoryId, slug) ||
    (item?.topicKey
      ? { topicKey: item.topicKey, title: item.title }
      : null);
  if (!topic) return null;
  if (topic.topicKey === 'vehicle_sell_vehicle' && !topic.draftMessage) {
    return {
      type: 'thread',
      topic: { ...topic, draftMessage: SELL_VEHICLE_DRAFT_MESSAGE },
    };
  }
  return { type: 'thread', topic };
}
